import React, { useState, useRef, useCallback, useId, useEffect } from 'react';
import './Tooltip.css';

export type TooltipPosition = 'top' | 'bottom' | 'left' | 'right';

export interface TooltipProps {
  /**
   * The content to display in the tooltip
   */
  content: React.ReactNode;
  /**
   * The element that triggers the tooltip
   */
  children: React.ReactElement<{ 'aria-describedby'?: string }>;
  /**
   * Preferred position of the tooltip relative to the trigger.
   * Will flip to opposite side if not enough space.
   * @default 'top'
   */
  position?: TooltipPosition;
  /**
   * Delay in ms before showing the tooltip
   * @default 200
   */
  showDelay?: number;
  /**
   * Delay in ms before hiding the tooltip
   * @default 0
   */
  hideDelay?: number;
  /**
   * Whether the tooltip is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Optional id for the tooltip
   */
  id?: string;
  /**
   * Optional additional CSS classes for the tooltip
   */
  className?: string;
  /** Inline styles for the wrapper */
  style?: React.CSSProperties;
}

const getOppositePosition = (position: TooltipPosition): TooltipPosition => {
  const opposites: Record<TooltipPosition, TooltipPosition> = {
    top: 'bottom',
    bottom: 'top',
    left: 'right',
    right: 'left',
  };
  return opposites[position];
};

const checkVerticalOverflow = (tooltipRect: DOMRect, position: 'top' | 'bottom'): boolean => {
  const padding = 8;
  if (position === 'top') {
    return tooltipRect.top < padding;
  }
  return tooltipRect.bottom > window.innerHeight - padding;
};

const checkHorizontalOverflow = (tooltipRect: DOMRect, position: 'left' | 'right'): boolean => {
  const padding = 8;
  if (position === 'left') {
    return tooltipRect.left < padding;
  }
  return tooltipRect.right > window.innerWidth - padding;
};

/**
 * Calculate horizontal offset needed to keep tooltip in viewport
 * Used for top/bottom positioned tooltips
 */
const calculateHorizontalOffset = (tooltipRect: DOMRect): number => {
  const padding = 8;
  if (tooltipRect.left < padding) {
    // Tooltip is going off left edge
    return padding - tooltipRect.left;
  }
  if (tooltipRect.right > window.innerWidth - padding) {
    // Tooltip is going off right edge
    return window.innerWidth - padding - tooltipRect.right;
  }
  return 0;
};

export const Tooltip: React.FC<TooltipProps> = ({
  content,
  children,
  position = 'top',
  showDelay = 200,
  hideDelay = 0,
  disabled = false,
  id,
  className = '',
  style,
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [actualPosition, setActualPosition] = useState(position);
  const [horizontalOffset, setHorizontalOffset] = useState(0);
  const showTimeoutRef = useRef<number | null>(null);
  const hideTimeoutRef = useRef<number | null>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const generatedId = useId();
  const tooltipId = id || `hi-ui-tooltip-${generatedId}`;

  // Reset position when preferred position changes
  useEffect(() => {
    setActualPosition(position);
  }, [position]);

  // Check for overflow and flip/adjust if needed
  useEffect(() => {
    if (isVisible && tooltipRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();

      // For top/bottom positions, check vertical overflow and flip if needed
      if (actualPosition === 'top' || actualPosition === 'bottom') {
        if (checkVerticalOverflow(tooltipRect, actualPosition)) {
          const opposite = getOppositePosition(actualPosition);
          if (opposite !== position || actualPosition === position) {
            setActualPosition(opposite);
          }
        }
        // Also calculate horizontal offset to keep tooltip in viewport
        const offset = calculateHorizontalOffset(tooltipRect);
        setHorizontalOffset(offset);
      } else {
        // For left/right positions, check horizontal overflow and flip if needed
        if (checkHorizontalOverflow(tooltipRect, actualPosition)) {
          const opposite = getOppositePosition(actualPosition);
          if (opposite !== position || actualPosition === position) {
            setActualPosition(opposite);
          }
        }
        setHorizontalOffset(0);
      }
    }
  }, [isVisible, actualPosition, position]);

  const clearTimeouts = useCallback(() => {
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = null;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const show = useCallback(() => {
    if (disabled) return;
    clearTimeouts();
    setActualPosition(position); // Reset to preferred position
    setHorizontalOffset(0); // Reset horizontal offset
    showTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(true);
    }, showDelay);
  }, [disabled, showDelay, clearTimeouts, position]);

  const hide = useCallback(() => {
    clearTimeouts();
    hideTimeoutRef.current = window.setTimeout(() => {
      setIsVisible(false);
    }, hideDelay);
  }, [hideDelay, clearTimeouts]);

  const handleMouseEnter = useCallback(() => {
    show();
  }, [show]);

  const handleMouseLeave = useCallback(() => {
    hide();
  }, [hide]);

  const handleFocus = useCallback(() => {
    show();
  }, [show]);

  const handleBlur = useCallback(() => {
    hide();
  }, [hide]);

  // Touch support: tap to show, any subsequent tap hides
  const handleTouchEnd = useCallback(
    (e: React.TouchEvent) => {
      if (disabled) return;
      // Only handle if not already visible - let document handler close it
      if (!isVisible) {
        e.preventDefault();
        setActualPosition(position);
        setIsVisible(true);
      }
    },
    [disabled, isVisible, position]
  );

  // Close tooltip on any tap when visible (mobile)
  useEffect(() => {
    if (!isVisible) return;

    const handleTouchAnywhere = () => {
      setIsVisible(false);
    };

    // Use setTimeout to avoid immediate close from the same tap that opened it
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('touchend', handleTouchAnywhere);
    }, 10);

    return () => {
      clearTimeout(timeoutId);
      document.removeEventListener('touchend', handleTouchAnywhere);
    };
  }, [isVisible]);

  const tooltipClasses = [
    'hi-ui-tooltip',
    `hi-ui-tooltip--${actualPosition}`,
    isVisible ? 'hi-ui-tooltip--visible' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <span
      ref={wrapperRef}
      className="hi-ui-tooltip-wrapper"
      style={style}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onTouchEnd={handleTouchEnd}
      data-testid="tooltip-wrapper"
    >
      {React.cloneElement(children, {
        'aria-describedby': isVisible ? tooltipId : undefined,
      })}
      <span
        ref={tooltipRef}
        id={tooltipId}
        role="tooltip"
        className={tooltipClasses}
        aria-hidden={!isVisible}
        data-testid="tooltip"
        style={
          horizontalOffset !== 0
            ? { transform: `translateX(calc(-50% + ${horizontalOffset}px))` }
            : undefined
        }
      >
        {content}
        <span
          className="hi-ui-tooltip__arrow"
          aria-hidden="true"
          style={
            horizontalOffset !== 0
              ? { transform: `translateX(calc(-50% - ${horizontalOffset}px))` }
              : undefined
          }
        />
      </span>
    </span>
  );
};
